/**
 * Firebase Realtime Database (RTDB) Signaling Service
 * 
 * This service handles WebRTC signaling via Firebase RTDB to align with the mobile app.
 * 
 * RTDB Path Structure:
 * /calls/{callId}/
 *   ├── participants/{userId}/ - presence tracking
 *   │   ├── joined: timestamp
 *   │   └── active: boolean
 *   ├── offers/{recipientUserId}/ - WebRTC offers
 *   │   ├── offer: { type: "offer", sdp: "..." }
 *   │   ├── from: callerUserId
 *   │   └── timestamp: timestamp
 *   ├── answers/{callerUserId}/ - WebRTC answers
 *   │   ├── answer: { type: "answer", sdp: "..." }
 *   │   ├── from: recipientUserId
 *   │   └── timestamp: timestamp
 *   ├── candidates/{targetUserId}/{pushId}/ - ICE candidates
 *   │   ├── candidate: RTCIceCandidateInit
 *   │   ├── from: senderUserId
 *   │   └── timestamp: timestamp
 *   ├── ended/{targetUserId}/ - call ended signals
 *   │   ├── from: senderUserId
 *   │   └── timestamp: timestamp
 *   └── cleanup/
 *       └── scheduledAt: timestamp
 */

import { realtimeDb } from 'auth/firebase';
import { 
  ref, 
  set, 
  push, 
  onValue, 
  onChildAdded, 
  onChildRemoved,
  remove, 
  serverTimestamp,
  onDisconnect,
  off,
  get
} from 'firebase/database';

// Types
export interface RTDBOffer {
  offer: RTCSessionDescriptionInit;
  from: string;
  timestamp: number;
}

export interface RTDBAnswer {
  answer: RTCSessionDescriptionInit;
  from: string;
  timestamp: number;
}

export interface RTDBCandidate {
  candidate: RTCIceCandidateInit;
  from: string;
  timestamp: number;
}

export interface RTDBEndedSignal {
  from: string;
  timestamp: number;
}

export interface RTDBParticipant {
  joined: number;
  active: boolean;
}

// Helper to get call ref
const getCallRef = (callId: string) => ref(realtimeDb, `calls/${callId}`);

/**
 * Register participant presence with automatic cleanup on disconnect
 */
export const registerParticipant = async (callId: string, userId: string): Promise<void> => {
  console.log('[RTDB] Registering participant:', userId, 'for call:', callId);
  
  const participantRef = ref(realtimeDb, `calls/${callId}/participants/${userId}`);
  
  // Set presence data
  await set(participantRef, {
    joined: serverTimestamp(),
    active: true
  });
  
  // Set up onDisconnect to remove presence when user disconnects
  await onDisconnect(participantRef).remove();
  
  console.log('[RTDB] Participant registered with onDisconnect handler');
};

/**
 * Unregister participant (manual cleanup)
 */
export const unregisterParticipant = async (callId: string, userId: string): Promise<void> => {
  console.log('[RTDB] Unregistering participant:', userId, 'from call:', callId);
  
  const participantRef = ref(realtimeDb, `calls/${callId}/participants/${userId}`);
  await remove(participantRef);
};

/**
 * Send WebRTC offer to recipient
 */
export const sendOffer = async (
  callId: string, 
  fromUserId: string, 
  toUserId: string, 
  offer: RTCSessionDescriptionInit
): Promise<void> => {
  console.log('[RTDB] Sending offer from:', fromUserId, 'to:', toUserId);
  console.log('[RTDB] Offer path:', `calls/${callId}/offers/${toUserId}`);
  console.log('[RTDB] Offer data:', JSON.stringify({
    offer: { type: offer.type, sdp: offer.sdp?.substring(0, 100) + '...' },
    from: fromUserId
  }));
  
  const offerRef = ref(realtimeDb, `calls/${callId}/offers/${toUserId}`);
  await set(offerRef, {
    offer: {
      type: offer.type,
      sdp: offer.sdp
    },
    from: fromUserId,
    timestamp: serverTimestamp()
  });
  
  console.log('[RTDB] Offer sent successfully');
  
  // Verify offer was written
  const snapshot = await get(offerRef);
  console.log('[RTDB] Offer verification - exists:', snapshot.exists());
};

/**
 * Send WebRTC answer to caller
 */
export const sendAnswer = async (
  callId: string, 
  fromUserId: string, 
  toUserId: string, 
  answer: RTCSessionDescriptionInit
): Promise<void> => {
  console.log('[RTDB] Sending answer from:', fromUserId, 'to:', toUserId);
  
  const answerRef = ref(realtimeDb, `calls/${callId}/answers/${toUserId}`);
  await set(answerRef, {
    answer,
    from: fromUserId,
    timestamp: serverTimestamp()
  });
  
  console.log('[RTDB] Answer sent successfully');
};

/**
 * Send ICE candidate to target user
 */
export const sendIceCandidate = async (
  callId: string, 
  fromUserId: string, 
  toUserId: string, 
  candidate: RTCIceCandidateInit
): Promise<void> => {
  console.log('[RTDB] Sending ICE candidate from:', fromUserId, 'to:', toUserId);
  
  const candidatesRef = ref(realtimeDb, `calls/${callId}/candidates/${toUserId}`);
  const newCandidateRef = push(candidatesRef);
  
  await set(newCandidateRef, {
    candidate,
    from: fromUserId,
    timestamp: serverTimestamp()
  });
  
  console.log('[RTDB] ICE candidate sent successfully');
};

/**
 * Send call ended signal to target user
 */
export const sendEndedSignal = async (
  callId: string, 
  fromUserId: string, 
  toUserId: string
): Promise<void> => {
  console.log('[RTDB] Sending ended signal from:', fromUserId, 'to:', toUserId);
  
  const endedRef = ref(realtimeDb, `calls/${callId}/ended/${toUserId}`);
  await set(endedRef, {
    from: fromUserId,
    timestamp: serverTimestamp()
  });
  
  console.log('[RTDB] Ended signal sent successfully');
};

/**
 * Subscribe to offers for current user
 * NOTE: This checks for existing offers first, then subscribes to new ones
 */
export const subscribeToOffers = (
  callId: string, 
  userId: string, 
  callback: (offer: RTDBOffer, fromUserId: string) => void
): (() => void) => {
  console.log('[RTDB] Subscribing to offers for user:', userId);
  
  const offerRef = ref(realtimeDb, `calls/${callId}/offers/${userId}`);
  let offerProcessed = false; // Track if we've already processed an offer
  
  // First, check for existing offer (in case it was written before we subscribed)
  get(offerRef).then((snapshot) => {
    if (snapshot.exists() && !offerProcessed) {
      offerProcessed = true;
      const data = snapshot.val() as RTDBOffer;
      console.log('[RTDB] Found EXISTING offer from:', data.from);
      callback(data, data.from);
      // Delete offer after delay to ensure it's fully processed
      setTimeout(() => {
        remove(offerRef).catch(err => console.error('[RTDB] Error removing existing offer:', err));
      }, 5000); // 5 second delay
    }
  }).catch(err => console.error('[RTDB] Error checking existing offer:', err));
  
  // Then subscribe to new offers
  const unsubscribe = onValue(offerRef, async (snapshot) => {
    if (snapshot.exists() && !offerProcessed) {
      offerProcessed = true;
      const data = snapshot.val() as RTDBOffer;
      console.log('[RTDB] Received NEW offer from:', data.from);
      callback(data, data.from);
      
      // Delete offer after delay to ensure it's fully processed
      setTimeout(() => {
        remove(offerRef).catch(err => console.error('[RTDB] Error removing offer:', err));
      }, 5000); // 5 second delay
    }
  });
  
  return () => {
    off(offerRef);
    console.log('[RTDB] Unsubscribed from offers');
  };
};

/**
 * Subscribe to answers for current user
 * NOTE: This checks for existing answers first, then subscribes to new ones
 */
export const subscribeToAnswers = (
  callId: string, 
  userId: string, 
  callback: (answer: RTDBAnswer, fromUserId: string) => void
): (() => void) => {
  const answerPath = `calls/${callId}/answers/${userId}`;
  console.log('[RTDB] Subscribing to answers at path:', answerPath);
  
  const answerRef = ref(realtimeDb, answerPath);
  let answerProcessed = false; // Track if we've already processed an answer
  
  // First, check for existing answer (in case it was written before we subscribed)
  get(answerRef).then((snapshot) => {
    console.log('[RTDB] Checking for existing answer at path:', answerPath, '- exists:', snapshot.exists());
    if (snapshot.exists() && !answerProcessed) {
      answerProcessed = true;
      const data = snapshot.val() as RTDBAnswer;
      console.log('[RTDB] Found EXISTING answer from:', data.from, 'data:', JSON.stringify(data));
      callback(data, data.from);
      // Delete answer after delay to ensure it's fully processed
      setTimeout(() => {
        remove(answerRef).catch(err => console.error('[RTDB] Error removing existing answer:', err));
      }, 5000); // 5 second delay
    }
  }).catch(err => console.error('[RTDB] Error checking existing answer:', err));
  
  // Then subscribe to new answers
  const unsubscribe = onValue(answerRef, async (snapshot) => {
    console.log('[RTDB] Answer listener triggered at path:', answerPath, '- exists:', snapshot.exists(), '- processed:', answerProcessed);
    if (snapshot.exists() && !answerProcessed) {
      answerProcessed = true;
      const data = snapshot.val() as RTDBAnswer;
      console.log('[RTDB] Received NEW answer from:', data.from, 'data:', JSON.stringify(data));
      callback(data, data.from);
      
      // Delete answer after delay to ensure it's fully processed
      setTimeout(() => {
        remove(answerRef).catch(err => console.error('[RTDB] Error removing answer:', err));
      }, 5000); // 5 second delay
    }
  });
  
  return () => {
    off(answerRef);
    console.log('[RTDB] Unsubscribed from answers');
  };
};

/**
 * Subscribe to ICE candidates for current user
 */
export const subscribeToIceCandidates = (
  callId: string, 
  userId: string, 
  callback: (candidate: RTCIceCandidateInit, fromUserId: string) => void
): (() => void) => {
  console.log('[RTDB] Subscribing to ICE candidates for user:', userId);
  
  const candidatesRef = ref(realtimeDb, `calls/${callId}/candidates/${userId}`);
  
  const unsubscribe = onChildAdded(candidatesRef, async (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.val() as RTDBCandidate;
      console.log('[RTDB] Received ICE candidate from:', data.from);
      callback(data.candidate, data.from);
      
      // Delete candidate after reading to keep RTDB clean
      await remove(snapshot.ref);
    }
  });
  
  return () => {
    off(candidatesRef);
    console.log('[RTDB] Unsubscribed from ICE candidates');
  };
};

/**
 * Subscribe to ended signals for current user
 */
export const subscribeToEndedSignals = (
  callId: string, 
  userId: string, 
  callback: (fromUserId: string) => void
): (() => void) => {
  console.log('[RTDB] Subscribing to ended signals for user:', userId);
  
  const endedRef = ref(realtimeDb, `calls/${callId}/ended/${userId}`);
  
  const unsubscribe = onValue(endedRef, async (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.val() as RTDBEndedSignal;
      console.log('[RTDB] Received ended signal from:', data.from);
      callback(data.from);
      
      // Delete ended signal after reading
      await remove(endedRef);
    }
  });
  
  return () => {
    off(endedRef);
    console.log('[RTDB] Unsubscribed from ended signals');
  };
};

/**
 * Subscribe to ANY ended signal written under `calls/{callId}/ended/*`.
 *
 * This complements `subscribeToEndedSignals` (which only listens to
 * the current user's ended-bucket). When a user accepts/declines/
 * ends on a NATIVE device while their WEB session is also ringing,
 * the native app typically writes the ended signal to the OTHER
 * party's bucket — so the web session of the same user never sees
 * the signal on its own bucket. Listening on the parent ended node
 * lets us treat ANY child write as "this call is over".
 *
 * NOTE: we DO NOT delete the signal here — that's the responsibility
 * of `subscribeToEndedSignals` for the targeted user (or backend
 * cleanup). We just observe.
 */
export const subscribeToAnyEndedSignal = (
  callId: string,
  callback: (targetUserId: string) => void,
): (() => void) => {
  console.log('[RTDB] Subscribing to ANY ended signals for call:', callId);
  const endedRef = ref(realtimeDb, `calls/${callId}/ended`);
  const unsub = onChildAdded(endedRef, (snapshot) => {
    const targetUserId = snapshot.key || '';
    console.log('[RTDB] ended child added for', targetUserId);
    callback(targetUserId);
  });
  return () => {
    try { unsub(); } catch { /* noop */ }
    off(endedRef);
    console.log('[RTDB] Unsubscribed from ANY ended signals for call:', callId);
  };
};

/**
 * Subscribe to participant changes
 */
export const subscribeToParticipants = (
  callId: string,
  onJoined: (userId: string) => void,
  onLeft: (userId: string) => void
): (() => void) => {
  console.log('[RTDB] Subscribing to participants for call:', callId);
  
  const participantsRef = ref(realtimeDb, `calls/${callId}/participants`);
  
  const unsubJoined = onChildAdded(participantsRef, (snapshot) => {
    const userId = snapshot.key;
    if (userId) {
      console.log('[RTDB] Participant joined:', userId);
      onJoined(userId);
    }
  });
  
  const unsubLeft = onChildRemoved(participantsRef, (snapshot) => {
    const userId = snapshot.key;
    if (userId) {
      console.log('[RTDB] Participant left:', userId);
      onLeft(userId);
    }
  });
  
  return () => {
    off(participantsRef);
    console.log('[RTDB] Unsubscribed from participants');
  };
};

/**
 * Clean up all RTDB data for a call
 */
export const cleanupCall = async (callId: string): Promise<void> => {
  console.log('[RTDB] Cleaning up call data:', callId);
  
  const callRef = getCallRef(callId);
  await remove(callRef);
  
  console.log('[RTDB] Call data cleaned up');
};

/**
 * Check if a call room exists in RTDB
 */
export const checkCallExists = async (callId: string): Promise<boolean> => {
  const callRef = getCallRef(callId);
  const snapshot = await get(callRef);
  return snapshot.exists();
};

/**
 * Combined subscription manager for all signaling events
 * Returns a cleanup function to unsubscribe from all
 */
export const subscribeToAllSignals = (
  callId: string,
  userId: string,
  handlers: {
    onOffer: (offer: RTCSessionDescriptionInit, fromUserId: string) => void;
    onAnswer: (answer: RTCSessionDescriptionInit, fromUserId: string) => void;
    onIceCandidate: (candidate: RTCIceCandidateInit, fromUserId: string) => void;
    onCallEnded: (fromUserId: string) => void;
    onParticipantJoined?: (userId: string) => void;
    onParticipantLeft?: (userId: string) => void;
  }
): (() => void) => {
  console.log('[RTDB] Setting up all signal subscriptions for call:', callId, 'user:', userId);
  
  const unsubscribers: (() => void)[] = [];
  
  // Subscribe to offers
  unsubscribers.push(
    subscribeToOffers(callId, userId, (data, from) => {
      handlers.onOffer(data.offer, from);
    })
  );
  
  // Subscribe to answers
  unsubscribers.push(
    subscribeToAnswers(callId, userId, (data, from) => {
      handlers.onAnswer(data.answer, from);
    })
  );
  
  // Subscribe to ICE candidates
  unsubscribers.push(
    subscribeToIceCandidates(callId, userId, handlers.onIceCandidate)
  );
  
  // Subscribe to ended signals
  unsubscribers.push(
    subscribeToEndedSignals(callId, userId, handlers.onCallEnded)
  );
  
  // Subscribe to participant changes if handlers provided
  if (handlers.onParticipantJoined || handlers.onParticipantLeft) {
    unsubscribers.push(
      subscribeToParticipants(
        callId,
        handlers.onParticipantJoined || (() => {}),
        handlers.onParticipantLeft || (() => {})
      )
    );
  }
  
  console.log('[RTDB] All signal subscriptions set up');
  
  // Return combined cleanup function
  return () => {
    console.log('[RTDB] Cleaning up all signal subscriptions');
    unsubscribers.forEach(unsub => unsub());
  };
};
